// NR · Scroll scene: scroll position -> frame of one sprite sheet, drawn on a canvas.
// - The sheet loads only after the page has loaded, once the scene is about a screen away, and never for
//   reduced motion or data saver (nothing competes with the LCP).
// - It is decoded off the main thread (createImageBitmap on the fetched blob), then cut into one small bitmap per
//   frame in idle time, so no single draw has to upload the whole sheet to the GPU.
// - Frames are drawn only in requestAnimationFrame and only while the scene is on screen.
// - No layout reads per frame: the scene's offset and height are measured when it resizes or comes into view, and
//   progress is the scroll position against them (Horizon's desktop .page-wrapper scroller or the mobile document
//   scroller). Progress bar and floor line are CSS scroll-driven animations (no script).

import { getIntersectionRoot, getScrollTop } from '@theme/scroll-container';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isMobile = window.matchMedia('(max-width: 749px)');

function saveData() {
  const c = navigator.connection;
  return Boolean(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
}

function afterLoad(fn) {
  if (document.readyState === 'complete') fn();
  else window.addEventListener('load', fn, { once: true });
}

async function decode(src) {
  if ('createImageBitmap' in window) {
    // Ask for WebP: with fetch's default Accept (*/*) Shopify's CDN answers with a much larger JPEG
    const response = await fetch(src, { headers: { Accept: 'image/webp,*/*' } });
    if (!response.ok) throw new Error(`sheet ${response.status}`);
    return createImageBitmap(await response.blob());
  }
  const img = new Image();
  img.decoding = 'async';
  img.src = src;
  await img.decode();
  return img;
}

const idle = () => new Promise((resolve) => ('requestIdleCallback' in window ? requestIdleCallback(resolve, { timeout: 300 }) : setTimeout(resolve, 32)));

// One small bitmap per frame, cut a few at a time in idle time. Drawing from the whole sheet would upload all of
// it to the GPU in one go on the first draw (a long task); a frame is a small upload.
async function cut(sheet, cols, count, fw, fh) {
  if (!('createImageBitmap' in window) || !(sheet instanceof ImageBitmap)) return null;
  const list = [];
  for (let i = 0; i < count; i++) {
    list.push(await createImageBitmap(sheet, (i % cols) * fw, Math.floor(i / cols) * fh, fw, fh));
    if (i % 6 === 5) await idle();
  }
  sheet.close();
  return list;
}

function initScene(root) {
  if (root.dataset.nrReady) return;
  root.dataset.nrReady = '1';

  const callouts = [...root.querySelectorAll('[data-at]')];
  if (reduceMotion.matches || saveData()) {
    // Still pose + every line visible (CSS handles the reduced-motion layout)
    callouts.forEach((c) => c.classList.add('is-on'));
    return;
  }

  const stage = root.querySelector('.nr-scrub__stage');
  const canvas = root.querySelector('.nr-scrub__canvas');
  const ctx = canvas?.getContext('2d', { alpha: false });
  const cols = Number(root.dataset.cols) || 6;
  const rows = Number(root.dataset.rows) || 6;
  const frames = Math.min(Number(root.dataset.frames) || cols * rows, cols * rows);

  let sheet = null;
  let fw = 0;
  let fh = 0;
  let shown = -1;
  let current = 0; // eased frame position
  let target = 0;
  let active = 0; // the server renders the first callout on
  let raf = 0;
  let visible = false;
  // Measured outside animation frames (resize / entering view), never per frame
  let rootTop = 0;
  let span = 0;
  const measure = () => {
    rootTop = root.getBoundingClientRect().top + getScrollTop();
    span = root.offsetHeight - stage.offsetHeight;
  };

  let cells = null; // per-frame bitmaps once cut
  const draw = (index) => {
    if (!ctx || index === shown || !(cells || sheet)) return;
    shown = index;
    if (cells) ctx.drawImage(cells[index], 0, 0, canvas.width, canvas.height);
    else ctx.drawImage(sheet, (index % cols) * fw, Math.floor(index / cols) * fh, fw, fh, 0, 0, canvas.width, canvas.height);
  };

  const update = () => {
    raf = 0;
    const progress = span > 0 ? Math.min(1, Math.max(0, (getScrollTop() - rootTop) / span)) : 0;

    // Only the latest callout reached is shown; they share one slot. Classes change only on a new callout, and then
    // every callout is set, so one reached mid-scene (a jump, Back) never leaves an earlier one showing.
    const pct = progress * 100;
    let next = 0;
    callouts.forEach((c, i) => {
      if (pct >= Number(c.dataset.at)) next = i;
    });
    if (next !== active) {
      callouts.forEach((c, i) => c.classList.toggle('is-on', i === next));
      active = next;
    }

    target = progress * (frames - 1);
    // Ease toward the target frame so a 36-frame sheet reads smooth
    current += (target - current) * 0.35;
    if (Math.abs(target - current) < 0.05) current = target;
    draw(Math.round(current));
    if (current !== target && visible) raf = requestAnimationFrame(update);
  };

  const onScroll = () => {
    if (visible && !raf) raf = requestAnimationFrame(update);
  };

  const ac = new AbortController();
  const opts = { passive: true, signal: ac.signal };
  window.addEventListener('scroll', onScroll, opts);
  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, opts);
  // Re-measure when the scene or anything above it changes size (the page's main grows or shrinks)
  const sized = new ResizeObserver(() => {
    measure();
    onScroll();
  });
  sized.observe(root);
  sized.observe(root.closest('main') || document.body);

  // Draw only while on screen
  const seen = new IntersectionObserver(([entry]) => {
    if (!root.isConnected) {
      ac.abort();
      seen.disconnect();
      sized.disconnect();
      return;
    }
    visible = entry.isIntersecting;
    if (visible) {
      measure();
      onScroll();
    }
  });
  seen.observe(root);

  const src = isMobile.matches ? root.dataset.srcM : root.dataset.srcD;
  if (!src || !ctx) return;

  afterLoad(() => {
    const near = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        near.disconnect();
        decode(src)
          .then(async (bitmap) => {
            fw = Math.floor(bitmap.width / cols);
            fh = Math.floor(bitmap.height / rows);
            cells = await cut(bitmap, cols, frames, fw, fh);
            if (!cells) sheet = bitmap; // no createImageBitmap: draw straight from the decoded <img>
            canvas.width = fw;
            canvas.height = fh;
            requestAnimationFrame(() => {
              shown = -1;
              draw(Math.round(current));
              root.classList.add('is-live');
            });
          })
          .catch(() => {
            /* sheet failed: the still stays */
          });
      },
      // Horizon scrolls .page-wrapper on desktop; observe against it so the margin isn't clipped
      // Half a screen ahead: not at page load on phones or desktop (the scene sits under the decal grid)
      { root: getIntersectionRoot(), rootMargin: '50% 0px' }
    );
    near.observe(root);
  });
}

document.querySelectorAll('[data-nr-scrub]').forEach(initScene);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-nr-scrub]').forEach(initScene);
});
